import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { titleForSql, type ResultEntry } from '../types';
import type { ColumnValue } from '../worker/protocol';
import { CellInspector, type InspectedCell } from './CellInspector';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';

interface ResultsGridProps {
  results: ResultEntry[];
  activeResultId: string | null;
  busy: boolean;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onCloseAll: () => void;
}

const ROW_HEIGHT = 24; // px — must match the table row's actual height
const OVERSCAN = 12;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

function renderCell(value: ColumnValue): {
  display: string;
  className: string;
} {
  if (value === null || value === undefined) {
    return { display: '<null>', className: 'italic text-[#606366]' };
  }
  if (typeof value === 'number') {
    return {
      display: Number.isFinite(value) ? String(value) : 'NaN',
      className: 'text-right text-[#6897bb] tabular-nums',
    };
  }
  if (typeof value === 'boolean') {
    return { display: value ? 'true' : 'false', className: 'text-[#cc7832]' };
  }
  if (value instanceof Uint8Array) {
    return {
      display: `<BLOB ${value.byteLength} B>`,
      className: 'italic text-[#9876aa]',
    };
  }
  const str = String(value);
  if (ISO_DATE_RE.test(str.trim())) {
    return { display: str, className: 'text-[#6a8759]' };
  }
  return { display: str, className: 'text-[#a9b7c6]' };
}

function compareValues(a: ColumnValue, b: ColumnValue): number {
  if (a === b) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1;
  return String(a).localeCompare(String(b));
}

function csvEscape(value: ColumnValue): string {
  if (value == null) return '';
  if (value instanceof Uint8Array) return `<BLOB ${value.byteLength} B>`;
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(columns: string[], rows: ColumnValue[][]): string {
  const header = columns.map(csvEscape).join(',');
  const body = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  return `${header}\n${body}`;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface SortState {
  columnIndex: number;
  direction: 'asc' | 'desc';
}

function tabLabel(entry: ResultEntry, idx: number): string {
  if (entry.error && !entry.sql) return 'Error';
  return titleForSql(entry.sql, `Result ${idx + 1}`);
}

export function ResultsGrid({
  results,
  activeResultId,
  busy,
  onSelect,
  onClose,
  onCloseAll,
}: ResultsGridProps) {
  const [sort, setSort] = useState<SortState | null>(null);
  const [filter, setFilter] = useState('');
  const [inspected, setInspected] = useState<InspectedCell | null>(null);
  const [selectedCellKey, setSelectedCellKey] = useState<string | null>(null);
  const [cellMenu, setCellMenu] = useState<{
    pos: { x: number; y: number };
    items: ContextMenuItem[];
  } | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const [copied, setCopied] = useState(false);

  const active = results.find((r) => r.id === activeResultId) ?? null;
  const result = active?.result ?? null;

  // Reset sort + filter + scroll + inspector when the visible result changes.
  useEffect(() => {
    setSort(null);
    setFilter('');
    setScrollTop(0);
    setInspected(null);
    setSelectedCellKey(null);
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [activeResultId]);

  // External system: observe the scroll container's size so the slice we
  // render adapts to window resize.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setViewportH(el.clientHeight);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setViewportH(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeResultId]);

  const filteredRows = useMemo(() => {
    if (!result) return [] as ColumnValue[][];
    if (!filter.trim()) return result.rows;
    const needle = filter.trim().toLowerCase();
    return result.rows.filter((row) =>
      row.some((cell) => {
        if (cell == null) return false;
        if (cell instanceof Uint8Array) return false;
        return String(cell).toLowerCase().includes(needle);
      }),
    );
  }, [result, filter]);

  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows;
    const idx = sort.columnIndex;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => compareValues(a[idx] ?? null, b[idx] ?? null) * dir);
  }, [filteredRows, sort]);

  const handleHeaderClick = useCallback((i: number) => {
    setSort((prev) => {
      if (!prev || prev.columnIndex !== i) return { columnIndex: i, direction: 'asc' };
      if (prev.direction === 'asc') return { columnIndex: i, direction: 'desc' };
      return null;
    });
  }, []);

  const copyCsv = useCallback(async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(toCsv(result.columns, sortedRows));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      downloadBlob(
        new Blob([toCsv(result.columns, sortedRows)], { type: 'text/csv' }),
        'results.csv',
      );
    }
  }, [result, sortedRows]);

  const exportCsv = useCallback(() => {
    if (!result) return;
    downloadBlob(
      new Blob([toCsv(result.columns, sortedRows)], {
        type: 'text/csv;charset=utf-8',
      }),
      `results-${Date.now()}.csv`,
    );
  }, [result, sortedRows]);

  const totalRows = sortedRows.length;
  const hasResult = !!result && result.columns.length > 0;

  // Tabs strip — always rendered, even when empty (collapses to a thin
  // band) so the chrome doesn't pop in/out across runs.
  const tabsStrip = (
    <div className="flex shrink-0 items-stretch border-b border-[#323232] bg-[#3c3f41]">
      {results.length === 0 ? (
        <div className="px-3 py-1 text-[11px] text-[#606366]">No results yet</div>
      ) : (
        <>
          <div className="flex flex-1 items-stretch overflow-x-auto">
            {results.map((entry, idx) => {
              const isActive = entry.id === activeResultId;
              const label = tabLabel(entry, idx);
              return (
                <div
                  key={entry.id}
                  className={
                    'group flex shrink-0 items-center gap-1 border-r border-[#323232] px-2 text-[11px] ' +
                    (isActive
                      ? 'bg-[#2b2b2b] text-[#e8e8e8]'
                      : 'bg-[#3c3f41] text-[#bbbbbb] hover:bg-[#4c5052]')
                  }
                >
                  <button
                    type="button"
                    onClick={() => onSelect(entry.id)}
                    className="flex items-center gap-1.5 py-1"
                    title={entry.sql || '(no SQL)'}
                  >
                    {entry.pending && (
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#5394ec]" />
                    )}
                    {entry.error && !entry.pending && (
                      <span className="h-1.5 w-1.5 rounded-full bg-[#ff8888]" />
                    )}
                    {!entry.pending && !entry.error && (
                      <span className="h-1.5 w-1.5 rounded-full bg-[#499c54]" />
                    )}
                    <span className="max-w-[200px] truncate">{label}</span>
                    {entry.result && (
                      <span className="ml-1 text-[10px] tabular-nums text-[#606366]">
                        {entry.result.rows.length}r
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(entry.id);
                    }}
                    aria-label="Close result"
                    title="Close result"
                    className="rounded px-1 text-[#606366] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
          {results.length > 1 && (
            <button
              type="button"
              onClick={onCloseAll}
              className="border-l border-[#323232] px-2 text-[11px] text-[#808080] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
              title="Close all results"
            >
              Close all
            </button>
          )}
        </>
      )}
    </div>
  );

  // Action bar (filter + copy + export) — only meaningful when an active
  // tab has a result set.
  const actionBar = (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#323232] bg-[#3c3f41] px-2 py-1">
      <div className="flex items-center gap-2 text-[11px] text-[#bbbbbb]">
        {hasResult && (
          <>
            <span className="rounded bg-[#2b2b2b] px-1.5 py-0.5 tabular-nums">
              {totalRows.toLocaleString()} row{totalRows === 1 ? '' : 's'}
              {filter && result && ` / ${result.rows.length.toLocaleString()}`}
            </span>
            {active?.tookMs != null && (
              <span className="rounded bg-[#2b2b2b] px-1.5 py-0.5 tabular-nums text-[#808080]">
                {active.tookMs.toFixed(1)} ms
              </span>
            )}
            {sort && result && (
              <span className="text-[10px] text-[#808080]">
                · sorted by {result.columns[sort.columnIndex]} {sort.direction}
              </span>
            )}
          </>
        )}
        {!hasResult && active?.message && <span className="text-[#bbbbbb]">{active.message}</span>}
      </div>
      <div className="flex items-center gap-1">
        {hasResult && (
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            className="w-40 rounded border border-[#4a4a4a] bg-[#2b2b2b] px-2 py-0.5 font-mono text-[11px] text-[#bbbbbb] outline-none focus:border-[#5394ec]"
          />
        )}
        <button
          type="button"
          onClick={copyCsv}
          disabled={!hasResult}
          className="rounded px-2 py-0.5 text-[10px] text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8] disabled:cursor-not-allowed disabled:opacity-40"
          title="Copy CSV to clipboard"
        >
          {copied ? '✓ copied' : 'Copy'}
        </button>
        <button
          type="button"
          onClick={exportCsv}
          disabled={!hasResult}
          className="rounded px-2 py-0.5 text-[10px] text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8] disabled:cursor-not-allowed disabled:opacity-40"
          title="Download as CSV"
        >
          Export
        </button>
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (!active) {
    body = (
      <div className="flex h-full items-center justify-center text-xs text-[#808080]">
        {busy ? 'Executing…' : 'Run a query to see results.'}
      </div>
    );
  } else if (active.pending) {
    body = (
      <div className="flex h-full items-center justify-center text-xs text-[#808080]">
        Executing…
      </div>
    );
  } else if (active.error) {
    body = (
      <div className="m-3 rounded border border-[#7a3a3a] bg-[#3a2424] p-3 font-mono text-xs text-[#ff8888]">
        {active.error}
      </div>
    );
  } else if (!result) {
    body = (
      <div className="m-3 text-xs text-[#bbbbbb]">
        {active.message ?? 'Statement executed (no result set).'}
      </div>
    );
  } else if (result.columns.length === 0) {
    body = <div className="m-3 text-xs text-[#bbbbbb]">Statement executed (no result set).</div>;
  } else {
    const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const visibleCount = Math.ceil(viewportH / ROW_HEIGHT) + OVERSCAN * 2;
    const endIdx = Math.min(totalRows, startIdx + visibleCount);
    const slice = sortedRows.slice(startIdx, endIdx);
    const topPad = startIdx * ROW_HEIGHT;
    const bottomPad = (totalRows - endIdx) * ROW_HEIGHT;

    body = (
      <div
        ref={scrollerRef}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        className="relative flex-1 overflow-auto bg-[#2b2b2b]"
      >
        <table className="min-w-full border-collapse text-[12px]">
          <thead className="sticky top-0 z-10 bg-[#3c3f41] text-left">
            <tr>
              <th className="border-b border-r border-[#323232] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#606366]">
                #
              </th>
              {result.columns.map((col, i) => {
                const sortDir = sort && sort.columnIndex === i ? sort.direction : null;
                return (
                  <th
                    key={`${col}-${i}`}
                    onClick={() => handleHeaderClick(i)}
                    className="cursor-pointer select-none border-b border-r border-[#323232] px-2 py-1 text-[11px] font-semibold text-[#bbbbbb] hover:bg-[#4c5052]"
                    title="Click to sort"
                  >
                    <span className="inline-flex items-center gap-1">
                      {col}
                      <span className="text-[10px] text-[#5394ec]">
                        {sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : ''}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {topPad > 0 && (
              <tr style={{ height: topPad }}>
                <td colSpan={result.columns.length + 1} />
              </tr>
            )}
            {slice.map((row, ri) => {
              const absIdx = startIdx + ri;
              return (
                <tr
                  key={absIdx}
                  className="border-b border-[#323232] hover:bg-[#3c3f41]"
                  style={{ height: ROW_HEIGHT }}
                >
                  <td className="border-r border-[#323232] bg-[#313335] px-2 text-right text-[10px] text-[#606366] tabular-nums">
                    {absIdx + 1}
                  </td>
                  {row.map((cell, ci) => {
                    const { display, className } = renderCell(cell);
                    const key = `${absIdx}:${ci}`;
                    const isSelected = selectedCellKey === key;
                    const colName = result.columns[ci] ?? '';
                    const cellRef: InspectedCell = {
                      column: colName,
                      rowIndex: absIdx,
                      value: cell,
                    };
                    const copyText =
                      cell == null
                        ? ''
                        : cell instanceof Uint8Array
                          ? `<BLOB ${cell.byteLength} bytes>`
                          : String(cell);
                    const sqlLiteral =
                      cell == null
                        ? 'NULL'
                        : typeof cell === 'number'
                          ? String(cell)
                          : typeof cell === 'boolean'
                            ? cell
                              ? '1'
                              : '0'
                            : `'${String(cell).replace(/'/g, "''")}'`;
                    return (
                      <td
                        key={ci}
                        onClick={() => {
                          setSelectedCellKey(key);
                          setInspected(cellRef);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setSelectedCellKey(key);
                          setCellMenu({
                            pos: { x: e.clientX, y: e.clientY },
                            items: [
                              {
                                label: 'Inspect cell',
                                onClick: () => setInspected(cellRef),
                              },
                              {
                                label: 'Copy value',
                                onClick: () => {
                                  navigator.clipboard?.writeText(copyText).catch(() => {});
                                },
                              },
                              {
                                label: `Copy as ${colName} = …`,
                                onClick: () => {
                                  navigator.clipboard
                                    ?.writeText(`"${colName}" = ${sqlLiteral}`)
                                    .catch(() => {});
                                },
                              },
                              {
                                label: 'Copy column name',
                                onClick: () => {
                                  navigator.clipboard?.writeText(colName).catch(() => {});
                                },
                              },
                            ],
                          });
                        }}
                        className={
                          `max-w-[480px] cursor-pointer truncate whitespace-nowrap border-r border-[#323232] px-2 font-mono ${className} ` +
                          (isSelected ? 'bg-[#214283] text-[#e8e8e8]' : '')
                        }
                        title={display}
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {bottomPad > 0 && (
              <tr style={{ height: bottomPad }}>
                <td colSpan={result.columns.length + 1} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {tabsStrip}
      {actionBar}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 flex-col overflow-hidden">
          {body}
          {result?.truncated && (
            <div className="border-t border-[#7a6a2a]/50 bg-[#3a3424] px-3 py-1 text-[11px] text-[#bbb529]">
              Result truncated — showing first {totalRows.toLocaleString()} rows.
            </div>
          )}
        </div>
        <CellInspector
          cell={inspected}
          onClose={() => {
            setInspected(null);
            setSelectedCellKey(null);
          }}
        />
      </div>
      <ContextMenu
        position={cellMenu?.pos ?? null}
        items={cellMenu?.items ?? []}
        onClose={() => setCellMenu(null)}
      />
    </div>
  );
}
